/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { useIntl } from 'react-intl';
import { SoftwareImpactSeverity, SoftwareQuality } from '~shared/types/clean-code-taxonomy';
import { IMPACT_SEVERITIES, SOFTWARE_QUALITIES } from '~sq-server-commons/helpers/constants';
import { useStandardExperienceModeQuery } from '~sq-server-commons/queries/mode';
import { IssueSeverity, IssueType } from '~sq-server-commons/types/issues';
import { SandboxSettingsIssueTypes, SandboxSettingsSoftwareQualities } from '../../types';
import { GenericQualitySelector } from './GenericQualitySelector';

type StandardModeSelectorProps = {
  hasAddedCriticalTypes: boolean;
  isInvalid: boolean;
  level: 'instance' | 'project';
  onTypesChange?: (types: SandboxSettingsIssueTypes) => void;
  types: SandboxSettingsIssueTypes;
};

type MQRModeSelectorProps = {
  hasAddedCriticalTypes: boolean;
  isInvalid: boolean;
  level: 'instance' | 'project';
  onQualitiesChange: (qualities: SandboxSettingsSoftwareQualities) => void;
  qualities: SandboxSettingsSoftwareQualities;
};

type SandboxSettingQualitiesSectionProps = StandardModeSelectorProps & MQRModeSelectorProps;

export default function SandboxSettingQualitiesSection({
  onQualitiesChange,
  onTypesChange,
  qualities,
  types,
  isInvalid,
  hasAddedCriticalTypes,
  level,
}: Readonly<SandboxSettingQualitiesSectionProps>) {
  const { data: isStandardExperience } = useStandardExperienceModeQuery();

  return isStandardExperience ? (
    <StandardModeSelector
      hasAddedCriticalTypes={hasAddedCriticalTypes}
      isInvalid={isInvalid}
      level={level}
      onTypesChange={onTypesChange}
      types={types}
    />
  ) : (
    <MQRModeSelector
      hasAddedCriticalTypes={hasAddedCriticalTypes}
      isInvalid={isInvalid}
      level={level}
      onQualitiesChange={onQualitiesChange}
      qualities={qualities}
    />
  );
}

function StandardModeSelector({
  types,
  onTypesChange,
  isInvalid,
  hasAddedCriticalTypes,
  level,
}: Readonly<StandardModeSelectorProps>) {
  const intl = useIntl();

  const config = {
    mainOptions: [IssueType.Vulnerability, IssueType.Bug, IssueType.CodeSmell],
    severityOptions: Object.values(IssueSeverity).map((severity) => ({
      value: severity,
      label: intl.formatMessage({ id: `severity.${severity}` }),
    })),
    getMainLabel: (type: IssueType) => intl.formatMessage({ id: `issue.type.${type}.plural` }),
    getControlLabel: (severities: IssueSeverity[], formatMessage: (id: string) => string) =>
      severities.map((s) => formatMessage(`severity.${s}`)).join(', '),
    title: 'settings.sandbox_issues.qualities.title.standard',
    description: `settings.sandbox_issues.qualities.description.${level}`,
    invalidTextKey: isInvalid ? 'settings.sandbox_issues.qualities.invalid.standard' : undefined,
  };

  // Transform data structure for generic component
  const selectedItems = types.map(({ type, severities }) => ({
    main: type,
    severities,
  }));

  const handleChange = (items: Array<{ main: IssueType; severities: IssueSeverity[] }>) => {
    const newTypes = items.map(({ main, severities }) => ({
      type: main,
      severities,
    }));
    onTypesChange?.(newTypes);
  };

  return (
    <GenericQualitySelector
      config={config}
      hasAddedCriticalTypes={hasAddedCriticalTypes}
      onChange={handleChange}
      selectedItems={selectedItems}
    />
  );
}

function MQRModeSelector({
  qualities,
  onQualitiesChange,
  isInvalid,
  level,
  hasAddedCriticalTypes,
}: Readonly<MQRModeSelectorProps>) {
  const intl = useIntl();

  const config = {
    mainOptions: SOFTWARE_QUALITIES,
    severityOptions: IMPACT_SEVERITIES.map((severity) => ({
      value: severity,
      label: intl.formatMessage({ id: `severity_impact.${severity}` }),
    })),
    getMainLabel: (quality: SoftwareQuality) =>
      intl.formatMessage({ id: `software_quality.${quality}` }),
    getControlLabel: (
      severities: SoftwareImpactSeverity[],
      formatMessage: (id: string) => string,
    ) => severities.map((s) => formatMessage(`severity_impact.${s}`)).join(', '),
    title: 'settings.sandbox_issues.qualities.title.mqr',
    invalidTextKey: isInvalid ? 'settings.sandbox_issues.qualities.invalid.mqr' : undefined,
    description: `settings.sandbox_issues.qualities.description.${level}`,
  };

  // Transform data structure for generic component
  const selectedItems = qualities.map(({ softwareQuality, impactSeverities }) => ({
    main: softwareQuality,
    severities: impactSeverities,
  }));

  const handleChange = (
    items: Array<{ main: SoftwareQuality; severities: SoftwareImpactSeverity[] }>,
  ) => {
    const newQualities = items.map(({ main, severities }) => ({
      softwareQuality: main,
      impactSeverities: severities,
    }));
    onQualitiesChange(newQualities);
  };

  return (
    <GenericQualitySelector
      config={config}
      hasAddedCriticalTypes={hasAddedCriticalTypes}
      onChange={handleChange}
      selectedItems={selectedItems}
    />
  );
}
